/**
 * ConflictResolutionModal
 *
 * Modal zur Auflösung von Sync-Konflikten
 * Zeigt lokale vs. Server Version und bietet Auflösungs-Optionen
 */

import React, { useMemo } from 'react';
import { FaServer, FaLaptop, FaCodeBranch, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { useConflictResolution, ConflictStrategy } from '../contexts/ConflictResolutionContext';
import { useLanguage } from '../contexts/LanguageContext';
import './ConflictResolutionModal.css';

const ConflictResolutionModal = () => {
  const { t } = useLanguage();
  const {
    activeConflict,
    isModalOpen,
    isResolving,
    closeModal,
    resolve,
    mergeVersions
  } = useConflictResolution();

  // Berechne die zusammengeführte Version
  const mergedVersion = useMemo(() => {
    if (!activeConflict?.conflict) return null;
    return mergeVersions(
      activeConflict.conflict.serverVersion,
      activeConflict.conflict.localVersion
    );
  }, [activeConflict, mergeVersions]);

  if (!isModalOpen || !activeConflict) return null;

  const { conflict, entityType } = activeConflict;
  const serverVersion = conflict?.serverVersion;
  const localVersion = conflict?.localVersion;

  // Formatiere Datum
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Rendere eine Version als lesbare Zusammenfassung
  const renderVersionSummary = (version, label) => {
    if (!version) return <p className="no-data">{t('noData') || 'Keine Daten'}</p>;

    return (
      <div className="version-summary">
        {version.name && (
          <div className="summary-row">
            <span className="label">{t('name') || 'Name'}:</span>
            <span className="value">{version.name}</span>
          </div>
        )}
        {version.items && (
          <div className="summary-row">
            <span className="label">{t('items') || 'Einträge'}:</span>
            <span className="value">{version.items.length}</span>
          </div>
        )}
        {version.updatedAt && (
          <div className="summary-row">
            <span className="label">{t('lastUpdate') || 'Letzte Änderung'}:</span>
            <span className="value">{formatDate(version.updatedAt)}</span>
          </div>
        )}
        {version.items && (
          <div className="items-preview">
            {version.items.slice(0, 5).map((item, idx) => (
              <span key={idx} className={`item-badge ${item.checked ? 'checked' : ''}`}>
                {item.name}
              </span>
            ))}
            {version.items.length > 5 && (
              <span className="more-badge">+{version.items.length - 5}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="conflict-modal-overlay" onClick={closeModal}>
      <div className="conflict-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="conflict-modal-header">
          <FaExclamationTriangle className="conflict-icon" />
          <h2>{t('syncConflict') || 'Sync-Konflikt'}</h2>
          <button className="close-btn" onClick={closeModal} disabled={isResolving}>
            <FaTimes />
          </button>
        </div>

        {/* Beschreibung */}
        <p className="conflict-description">
          {t('conflictDescription') ||
            'Diese Daten wurden an anderer Stelle geändert, während du offline warst. Wie möchtest du fortfahren?'}
        </p>

        {/* Entity Info */}
        <div className="entity-info">
          <span className="entity-type">{entityType}</span>
        </div>

        {/* Versionen vergleichen */}
        <div className="versions-container">
          {/* Server Version */}
          <div className="version-card server">
            <div className="version-header">
              <FaServer />
              <h3>{t('serverVersion') || 'Server Version'}</h3>
            </div>
            {renderVersionSummary(serverVersion)}
          </div>

          {/* Lokale Version */}
          <div className="version-card local">
            <div className="version-header">
              <FaLaptop />
              <h3>{t('localVersion') || 'Deine Version'}</h3>
            </div>
            {renderVersionSummary(localVersion)}
          </div>
        </div>

        {/* Aktionen */}
        <div className="conflict-actions">
          <button
            className="action-btn server-btn"
            onClick={() => resolve(ConflictStrategy.SERVER)}
            disabled={isResolving}
          >
            <FaServer />
            <span>{t('keepServer') || 'Server übernehmen'}</span>
          </button>

          <button
            className="action-btn local-btn"
            onClick={() => resolve(ConflictStrategy.LOCAL)}
            disabled={isResolving}
          >
            <FaLaptop />
            <span>{t('keepLocal') || 'Meine behalten'}</span>
          </button>

          <button
            className="action-btn merge-btn"
            onClick={() => resolve(ConflictStrategy.MERGE, mergedVersion)}
            disabled={isResolving}
          >
            <FaCodeBranch />
            <span>{t('mergeChanges') || 'Zusammenführen'}</span>
          </button>
        </div>

        {/* Loading Indicator */}
        {isResolving && (
          <div className="resolving-overlay">
            <div className="spinner"></div>
            <span>{t('resolving') || 'Wird aufgelöst...'}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConflictResolutionModal;
